package er.prateek.crossly.progression

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import er.prateek.crossly.content.ContentRepository
import er.prateek.crossly.content.RemoteContentSource
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.config.ModesConfig
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.data.model.StreakInfo
import er.prateek.crossly.persistence.ContentCacheStore
import er.prateek.crossly.persistence.ProgressStore
import kotlin.random.Random
import kotlin.time.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

/**
 * NYT-style daily puzzle: the same puzzle for everyone on a given UTC date,
 * date-seeded from the bundled EASY pool so it works fully offline (no
 * dedicated daily-puzzle content yet -- that's Phase 5's remote
 * `daily/{date}.json`). Streak-tracked independently of level progress:
 * playing the daily never touches [ProgressionManager]'s stars/unlocks for
 * the underlying puzzle id, and level play never touches the streak.
 */
class DailyPuzzleManager(
    private val store: ProgressStore,
    private val contentRepository: ContentRepository,
    private val clock: Clock = Clock.System,
    private val contentCache: ContentCacheStore? = null,
    private val remote: RemoteContentSource? = null,
) {
    private var streakInfo by mutableStateOf(store.load().dailyStreak)

    /** Compose-observable; read from a @Composable to react to streak changes. */
    fun currentStreak(): StreakInfo = streakInfo

    /**
     * Prefers, in order: a cached puzzle already fetched for today, a live
     * fetch of the manifest's `dailyPuzzleUrlTemplate` (cached once fetched),
     * then the deterministic date-seeded EASY-pool pick if remote content is
     * unavailable or unconfigured -- Daily mode is never fully blocked offline.
     */
    suspend fun todaysPuzzle(): Puzzle {
        val todayIso = todayUtc().toString()

        contentCache?.loadDailyPuzzle(todayIso)?.let { return it }

        val template = contentCache?.loadManifest()?.dailyPuzzleUrlTemplate
        if (template != null && remote != null) {
            remote.fetchDailyPuzzle(template, todayIso)?.let { fetched ->
                contentCache.saveDailyPuzzle(todayIso, fetched)
                return fetched
            }
        }

        return fallbackPuzzle(todayIso)
    }

    /**
     * A fresh random challenge for the Daily button: a word crossword, a math crossword level or a
     * riddle set, picked each time by the relative weights in `modes.daily`. Math and riddle levels
     * are drawn only from ones the player has unlocked, so Daily never jumps ahead of their
     * progress. A mode switched off in [modes], weighted `0`, or with nothing unlocked yet drops
     * out and its share goes to the rest; with nothing left, it's a word crossword.
     */
    suspend fun randomChallenge(
        math: ModeProgressionManager,
        riddles: ModeProgressionManager,
        modes: ModesConfig,
        random: Random = Random,
    ): DailyChallenge {
        val mix = modes.daily
        val mathLevel = modes.mathCrossword.takeIf { it.enabled && mix.mathCrosswordWeight > 0 }
            ?.let { randomUnlockedLevel(math, it.levelsPerTier, random) }
        val riddleLevel = modes.riddles.takeIf { it.enabled && mix.riddlesWeight > 0 }
            ?.let { randomUnlockedLevel(riddles, it.levelsPerTier, random) }
        val choices = buildList {
            add(DailyKind.CROSSWORD to mix.crosswordWeight)
            if (mathLevel != null) add(DailyKind.MATH_CROSSWORD to mix.mathCrosswordWeight)
            if (riddleLevel != null) add(DailyKind.RIDDLES to mix.riddlesWeight)
        }
        return when (weightedPick(choices, random) ?: DailyKind.CROSSWORD) {
            DailyKind.CROSSWORD -> randomCrossword(random)
            DailyKind.MATH_CROSSWORD -> DailyChallenge.MathCrossword(mathLevel!!)
            DailyKind.RIDDLES -> DailyChallenge.Riddles(riddleLevel!!)
        }
    }

    private suspend fun randomCrossword(random: Random): DailyChallenge {
        val pool = listOf(DifficultyTier.EASY, DifficultyTier.MEDIUM).flatMap { contentRepository.puzzlesByTier(it) }
        val puzzle = pool.randomOrNull(random) ?: fallbackPuzzle(todayUtc().toString())
        return DailyChallenge.Crossword(puzzle)
    }

    private fun randomUnlockedLevel(progression: ModeProgressionManager, levelsPerTier: Int, random: Random): String? =
        DifficultyTier.entries
            .flatMap { tier -> (1..levelsPerTier).map { tier to it } }
            .filter { (tier, index) -> progression.isLevelUnlocked(tier, index - 1) }
            .randomOrNull(random)
            ?.let { (tier, index) -> progression.levelId(tier, index) }

    private suspend fun fallbackPuzzle(todayIso: String): Puzzle {
        val pool = contentRepository.puzzlesByTier(DifficultyTier.EASY)
        val dayIndex = LocalDate.parse(todayIso).toEpochDays().mod(pool.size)
        return pool[dayIndex]
    }

    fun hasCompletedToday(): Boolean = streakInfo.lastCompletedDate == todayUtc().toString()

    fun todayIso(): String = todayUtc().toString()

    /** Idempotent: replaying an already-completed daily doesn't double-count the streak. */
    fun recordCompletion(): StreakInfo {
        val today = todayUtc()
        val todayIso = today.toString()
        if (streakInfo.lastCompletedDate == todayIso) return streakInfo

        val yesterdayIso = LocalDate.fromEpochDays(today.toEpochDays() - 1).toString()
        val newCurrent = if (streakInfo.lastCompletedDate == yesterdayIso) streakInfo.currentStreak + 1 else 1
        val updated = StreakInfo(
            currentStreak = newCurrent,
            longestStreak = maxOf(newCurrent, streakInfo.longestStreak),
            lastCompletedDate = todayIso,
        )
        streakInfo = updated
        store.save(store.load().copy(dailyStreak = updated))
        return updated
    }

    /** Forgets the daily-puzzle record (streak and "solved today"), for a full progress reset. */
    fun resetStreak() {
        streakInfo = StreakInfo()
        store.save(store.load().copy(dailyStreak = streakInfo))
    }

    private fun todayUtc(): LocalDate = clock.todayIn(TimeZone.UTC)
}

internal enum class DailyKind { CROSSWORD, MATH_CROSSWORD, RIDDLES }

/**
 * Picks one item with probability proportional to its weight; zero and negative weights never
 * win. `null` when nothing has a positive weight.
 */
internal fun <T> weightedPick(choices: List<Pair<T, Int>>, random: Random): T? {
    val live = choices.filter { it.second > 0 }
    if (live.isEmpty()) return null
    var roll = random.nextInt(live.sumOf { it.second })
    for ((item, weight) in live) {
        if (roll < weight) return item
        roll -= weight
    }
    return live.last().first
}

/** What the Daily button resolved to for this attempt. */
sealed interface DailyChallenge {
    data class Crossword(val puzzle: Puzzle) : DailyChallenge
    data class MathCrossword(val levelId: String) : DailyChallenge
    data class Riddles(val levelId: String) : DailyChallenge
}
